import { textOfStoredTurn } from '../../lib/turn-recovery';
import type { ChatAttachment, ChatMessage, WidgetEvent } from '../../lib/chat-api';
import type { ToolCall } from '../../lib/editor-chat';

/* Pure message-mapping helpers extracted from Chat.tsx (decomposition, Phase 4). */
/* Behaviour-identical — Chat.tsx re-imports these. Assistant messages persist */
/* interactive widgets inside `attachments` as { kind:'widget', ... }; on load we */
/* split them into `widgets` so the bubble renders them as widgets (not images) */
/* and the thread replays them. */

export type UiMessage = ChatMessage & { widgets?: WidgetEvent[]; tools?: ToolCall[] };

/* Render heights for the dedicated cours-workflow widgets (mirrors the server's */
/* COURS_WIDGET_HEIGHT — applied on replay where the SSE height isn't available). */
export const COURS_HEIGHTS: Record<string, number> = {
  cours_intent: 320,
  cours_config: 560,
  cours_refine: 420,
  cours_coherence: 700,
};

/* Tool names whose calls are WIDGETS (rendered as an iframe / Mcq), not data-tool */
/* ToolBlocks. Used to split a persisted message's metadata.tools on reload AND to */
/* keep the live stream from double-listing them as tools. */
export const WIDGET_TOOL_NAMES = new Set<string>([
  'render_widget',
  'render_interactive',
  'ask_mcq',
  'ask_form',
  ...Object.keys(COURS_HEIGHTS),
]);

export function toWidget(a: { name?: string; toolCallId?: string; args?: unknown; voice?: boolean }): WidgetEvent {
  const wa = (a.args ?? {}) as { html?: string; title?: string };
  return {
    name: a.name as WidgetEvent['name'],
    toolCallId: a.toolCallId,
    interactive: a.name !== 'render_widget',
    args: a.args,
    html: wa.html, /* lift html/title to top level so the renderer finds them */
    title: wa.title,
    height: a.name ? COURS_HEIGHTS[a.name] : undefined,
    voice: a.voice, /* voice widgets answer over the Live WS, not a resume request */
  } as WidgetEvent;
}

export function toUiMessage(stored: ChatMessage): UiMessage {
  const text = textOfStoredTurn(stored.transcript, stored.turn_failed);
  const m = text === stored.transcript ? stored : { ...stored, transcript: text };
  const atts = (m.attachments ?? []) as Array<
    ChatAttachment & { kind?: string; name?: string; toolCallId?: string; args?: unknown; voice?: boolean }
  >;
  const widgetAtts = atts.filter((a) => a && a.kind === 'widget');
  const allTools = (m.metadata?.tools ?? []) as Array<ToolCall & { name?: string; args?: unknown }>;
  /* A render_widget/render_interactive/ask_mcq/cours_* call is a WIDGET, not a */
  /* data-tool block: it's persisted in metadata.tools (with its html in args), so */
  /* on reload we reconstruct it as a widget — otherwise it'd render as an (empty) */
  /* ToolBlock and the visual would be lost after a refresh. */
  const widgetTools = allTools.filter((t) => t.name != null && WIDGET_TOOL_NAMES.has(t.name));
  const dataTools = allTools.filter((t) => t.name == null || !WIDGET_TOOL_NAMES.has(t.name));
  const widgets = [...widgetAtts, ...widgetTools].map(toWidget);
  if (widgets.length === 0 && dataTools.length === 0) return m;
  return {
    ...m,
    attachments: atts.filter((a) => !a.kind) as ChatAttachment[],
    widgets: widgets.length ? widgets : undefined,
    tools: dataTools.length ? dataTools : undefined,
  };
}
